import { useRouter, useRouterState } from '@tanstack/react-router';
import { Briefcase, Building2, CalendarClock, CornerDownLeft, FileSignature, FileText, FolderHeart, Search, Users, Waypoints, type LucideIcon } from 'lucide-react';
import { RECENT_RECORD_KIND_LABELS, SEARCH_TYPE_LABELS, type RecentRecordKind, type SearchType } from '@nest-recruit/shared';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Keys } from '../components/Kbd';
import { fuzzyMatch, highlightSegments } from '../lib/fuzzy';
import { useRecents } from '../stores/recents';
import { useCurrentSession } from '../stores/session';
import { useUi } from '../stores/ui';
import {
  COMMAND_SECTIONS,
  COMMAND_SECTION_LABELS,
  commandOrder,
  getCommands,
  isContextual,
  subscribeToCommands,
  visibleCommands,
  type Command,
  type CommandSection,
} from './command-registry';
import styles from './CommandPalette.module.css';
import { usePaletteSearch } from './use-palette-search';
import { searchScopeLabel, useModules } from '../lib/modules';

// Ctrl/Cmd+K. 06-qol §1 and §9: opens in under 100ms with no network call on
// open, fuzzy matching rather than prefix, three sections in a fixed order, and
// results permission-filtered BEFORE display.
//
// The index is already in memory — `getCommands()` reads a module-level Map that
// feature slices populated at import time. Nothing here fetches, and nothing may.

/** Per section, so one large section cannot bury the others below the fold. */
const MAX_PER_SECTION = 8;

interface Ranked {
  readonly command: Command;
  readonly score: number;
  readonly indices: readonly number[];
}

/** One heading and its rows — a command section, or one kind of record from search (S25). */
interface Block {
  readonly key: string;
  readonly label: string;
  readonly rows: readonly Ranked[];
}

const SEARCH_ICONS: Readonly<Record<SearchType, LucideIcon>> = {
  candidates: Users,
  jobs: Briefcase,
  requisitions: Building2,
  pools: FolderHeart,
};

const RECENT_ICONS: Readonly<Record<RecentRecordKind, LucideIcon>> = {
  candidate: Users,
  application: FileText,
  job: Briefcase,
  requisition: Building2,
  offer: FileSignature,
  interview: CalendarClock,
  pool: FolderHeart,
  position: Waypoints,
};

const LISTBOX_ID = 'command-palette-results';
const optionId = (index: number): string => `command-palette-option-${index}`;

/**
 * The panel is a separate component that only exists while the palette is open, so
 * its query and cursor are fresh on every open by construction. Resetting them in
 * an effect instead would render once with the previous search still showing.
 */
export function CommandPalette(): ReactNode {
  const isOpen = useUi((state) => state.isPaletteOpen);
  if (!isOpen) return null;
  return <PalettePanel />;
}

function PalettePanel(): ReactNode {
  const close = useUi((state) => state.closePalette);
  const session = useCurrentSession();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [lastQuery, setLastQuery] = useState('');
  const router = useRouter();
  const search = usePaletteSearch(query);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  // Re-reads whenever a feature module registers or unregisters.
  const commands = useSyncExternalStore(subscribeToCommands, getCommands, getCommands);
  const modules = useModules();

  // The screen a contextual action belongs to: the deepest route that matched (S32).
  const currentScreenId = useRouterState({ select: (state) => state.matches.at(-1)?.routeId ?? null });
  const recents = useRecents((state) => state.records);

  // Filtering moves the list under the cursor, so the top result becomes the one
  // Enter should take. Adjusted during render rather than in an effect — an effect
  // would paint one frame with the cursor still on the old row.
  if (query !== lastQuery) {
    setLastQuery(query);
    setActiveIndex(0);
  }

  const grouped = useMemo(() => {
    const allowed = visibleCommands(commands, session, currentScreenId, modules);
    const result = new Map<CommandSection, Ranked[]>();

    for (const section of COMMAND_SECTIONS) {
      const ranked: Ranked[] = [];
      for (const command of allowed) {
        if (command.section !== section) continue;
        // Match against the label plus its context and keywords, so typing a job
        // name finds the candidates on it.
        const haystack = [command.label, command.context ?? '', ...(command.keywords ?? [])].join(
          ' ',
        );
        const match = fuzzyMatch(query, haystack);
        if (!match) continue;
        // Only indices inside the label can be highlighted; the rest matched the
        // hidden context and simply do not render a hit.
        const labelIndices = match.indices.filter((index) => index < command.label.length);
        ranked.push({ command, score: match.score, indices: labelIndices });
      }

      ranked.sort((a, b) => {
        // Contextual actions come first in the Actions section (06-qol §1).
        const aContext = isContextual(a.command, currentScreenId) ? 1 : 0;
        const bContext = isContextual(b.command, currentScreenId) ? 1 : 0;
        if (aContext !== bContext) return bContext - aContext;
        if (b.score !== a.score) return b.score - a.score;
        // Registration order last, so equal scores never shuffle between keystrokes.
        return commandOrder(a.command.id) - commandOrder(b.command.id);
      });

      if (ranked.length > 0) result.set(section, ranked.slice(0, MAX_PER_SECTION));
    }

    // 06-qol §1 (Q29): the last ten records opened, matched like any command.
    const recentRows = recents.flatMap((record): Ranked[] => {
      const context = [RECENT_RECORD_KIND_LABELS[record.kind], record.subtitle].filter(Boolean).join(' · ');
      const match = fuzzyMatch(query, `${record.title} ${context}`);
      if (!match) return [];
      const command: Command = { id: `recent.${record.id}`, label: record.title, section: 'recent', context, icon: RECENT_ICONS[record.kind], run: () => router.history.push(record.path) };
      return [{ command, score: match.score, indices: match.indices.filter((index) => index < record.title.length) }];
    });
    if (query !== '') recentRows.sort((a, b) => b.score - a.score);
    if (recentRows.length > 0) result.set('recent', [...(result.get('recent') ?? []), ...recentRows].slice(0, MAX_PER_SECTION));
    return result;
  }, [commands, query, session, currentScreenId, modules, recents, router]);

  /**
   * F20.04 (S25 Q2): records from the server, five of each kind, between the actions and the
   * screens — and "See all results" to the full page. Already permission-filtered by the API.
   */
  const searchBlocks = useMemo((): readonly Block[] => {
    if (!search.result) return [];
    const blocks: Block[] = search.result.groups
      .filter((group) => group.hits.length > 0)
      .map((group) => ({
        key: `search-${group.type}`,
        label: `${SEARCH_TYPE_LABELS[group.type]}${group.total > group.hits.length ? ` · ${group.hits.length} of ${group.total}` : ''}`,
        rows: group.hits.map((hit) => ({
          command: {
            id: `search.${group.type}.${hit.id}`,
            label: hit.title,
            section: 'navigate' as const,
            ...(hit.subtitle ? { context: hit.subtitle } : {}),
            icon: SEARCH_ICONS[group.type],
            run: () => router.history.push(hit.path),
          },
          score: 0,
          indices: [],
        })),
      }));
    return blocks;
  }, [search.result, router]);

  /** Last, so Enter on a screen's name opens the screen rather than the search page. */
  const searchAllBlock = useMemo((): Block | null => {
    if (!search.result) return null;
    return {
      key: 'search-all',
      label: 'Search',
      rows: [
        {
          command: {
            id: 'search.all',
            label: `See all results for "${search.term}"`,
            section: 'navigate',
            context: searchScopeLabel(modules),
            icon: Search,
            run: () => router.history.push(`/search?q=${encodeURIComponent(search.term)}`),
          },
          score: 0,
          indices: [],
        },
      ],
    };
  }, [search.result, search.term, router, modules]);

  /** Actions, Recent, the search results, then Navigate — the order the rows render in. */
  const blocks = useMemo((): readonly Block[] => {
    const ofSection = (section: CommandSection): Block | null => {
      const rows = grouped.get(section);
      return rows ? { key: section, label: COMMAND_SECTION_LABELS[section], rows } : null;
    };
    return [ofSection('action'), ofSection('recent'), ...searchBlocks, ofSection('navigate'), searchAllBlock].filter((block): block is Block => block !== null);
  }, [grouped, searchBlocks, searchAllBlock]);

  /** Flattened, in render order — what the arrow keys actually walk. */
  const flat = useMemo(() => blocks.flatMap((block) => block.rows), [blocks]);

  // The sections keep their fixed order (06-qol §1), but typing a thing's exact name means that
  // thing: "jobs" + Enter opens the job list, not the first action or record that matched (S32).
  const exactIndex = useMemo(() => {
    const wanted = query.trim().toLowerCase();
    return wanted === '' ? -1 : flat.findIndex((row) => row.command.label.toLowerCase() === wanted);
  }, [flat, query]);
  // Keyed on where the match sits too: records arriving from the server move it down the list.
  const exactKey = `${query}:${exactIndex}`;
  const [cursorKey, setCursorKey] = useState('');
  if (exactIndex >= 0 && cursorKey !== exactKey) {
    setCursorKey(exactKey);
    setActiveIndex(exactIndex);
  }

  useEffect(() => {
    openerRef.current = document.activeElement as HTMLElement | null;
    // Focus after paint, so the entry animation is not interrupted by a mobile
    // keyboard scrolling the panel into view.
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      cancelAnimationFrame(frame);
      // Focus returns to whatever opened it (06-qol §2).
      openerRef.current?.focus();
    };
  }, []);

  // Keep the active row in view as the arrow keys run past the fold.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const run = useCallback(
    (command: Command) => {
      close();
      command.run();
    },
    [close],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
        event.preventDefault();
        setActiveIndex((index) => (flat.length === 0 ? 0 : (index + 1) % flat.length));
        return;
      }
      if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
        event.preventDefault();
        setActiveIndex((index) =>
          flat.length === 0 ? 0 : (index - 1 + flat.length) % flat.length,
        );
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const selected = flat[activeIndex];
        if (selected) run(selected.command);
      }
    },
    [flat, activeIndex, close, run],
  );

  let cursor = -1;

  return createPortal(
    <div
      className={styles.overlay}
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onKeyDown={onKeyDown}
      >
        <div className={styles.inputRow}>
          <Search size={18} aria-hidden />
          <input
            ref={inputRef}
            className={styles.input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search actions, records and screens"
            aria-label="Search actions, records and screens"
            role="combobox"
            aria-expanded={flat.length > 0}
            aria-controls={LISTBOX_ID}
            aria-autocomplete="list"
            aria-activedescendant={flat.length > 0 ? optionId(activeIndex) : undefined}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className={styles.results} ref={listRef} id={LISTBOX_ID} role="listbox" aria-label="Results">
          {flat.length === 0 ? (
            <div className={styles.empty}>
              <span className={styles.emptyTitle}>{search.isSearching ? `Searching for “${query}”` : <>Nothing matches &ldquo;{query}&rdquo;</>}</span>
              <span className={styles.emptyHint}>
                Try fewer letters, or a different word. The palette only ever shows things you
                have permission to open.
              </span>
            </div>
          ) : (
            blocks.map((block) => (
              <div key={block.key} role="group" aria-labelledby={`${LISTBOX_ID}-${block.key}`}>
                <div className={styles.sectionHead} id={`${LISTBOX_ID}-${block.key}`} role="presentation">
                  <span>{block.label}</span>
                </div>
                {block.rows.map((row) => {
                  cursor += 1;
                  const index = cursor;
                  const isActive = index === activeIndex;
                  const Icon = row.command.icon;
                  return (
                    <button
                      key={row.command.id}
                      id={optionId(index)}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      tabIndex={-1}
                      data-active={isActive}
                      className={`${styles.row} ${isActive ? styles.rowActive : ''}`}
                      onMouseMove={() => setActiveIndex(index)}
                      onClick={() => run(row.command)}
                    >
                      {Icon && (
                        <span className={styles.rowIcon} aria-hidden>
                          <Icon size={16} />
                        </span>
                      )}
                      <span className={styles.rowText}>
                        <span className={styles.rowLabel}>
                          {highlightSegments(row.command.label, row.indices).map((segment, i) =>
                            segment.matched ? (
                              <span key={i} className={styles.hit}>
                                {segment.text}
                              </span>
                            ) : (
                              <span key={i}>{segment.text}</span>
                            ),
                          )}
                        </span>
                        {row.command.context !== undefined && (
                          <span className={styles.rowContext}>{row.command.context}</span>
                        )}
                      </span>
                      {row.command.shortcut !== undefined && (
                        <span className={styles.rowShortcut} aria-hidden>
                          <Keys keys={row.command.shortcut.split(' ')} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className={styles.footer}>
          <span className={styles.footerItem}>
            <Keys keys={['↑', '↓']} /> move
          </span>
          <span className={styles.footerItem}>
            <CornerDownLeft size={12} aria-hidden /> open
          </span>
          <span className={styles.footerItem}>
            <Keys keys={['Tab']} /> next
          </span>
          <span className={styles.footerItem}>
            <Keys keys={['Esc']} /> close
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
